import React, { useState } from 'react';
import { Card, Badge, Collapse, Row, Col, Table } from 'react-bootstrap';
import { FaServer, FaSpinner, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { safeRenderValue } from './utils/logsUtils';

const LambdaLogsCard = ({ lambdaLogs, fetchingLambdaLogs }) => {
  const [expandedLambdaFunctions, setExpandedLambdaFunctions] = useState({});

  if (!lambdaLogs) return null;

  const toggleLambdaFunction = (functionName) => {
    setExpandedLambdaFunctions(prev => ({
      ...prev,
      [functionName]: !prev[functionName]
    }));
  };

  return (
    <Card className="logs-card mb-4">
      <Card.Header>
        <h2 className="section-heading">
          <FaServer className="me-2" />
          Lambda Logs
          {fetchingLambdaLogs && <FaSpinner className="fa-spin ms-2" />}
        </h2>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="lambda-logs-content p-4">
          {Object.entries(lambdaLogs).map(([functionName, functionLogs]) => (
            <Card key={functionName} className="mb-4">
              <Card.Header 
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleLambdaFunction(functionName)}
              >
                <div className="d-flex align-items-center">
                  <strong className="me-3">{functionLogs.function_name || functionName}</strong>
                  <Badge bg="info" className="me-3">
                    {functionLogs.invocation_count} invocation{functionLogs.invocation_count !== 1 ? 's' : ''}
                  </Badge>
                  <Badge 
                    bg={functionLogs.status === 'VERIFIED' ? 'success' : 'secondary'}
                  >
                    {functionLogs.status}
                  </Badge>
                </div>
                {expandedLambdaFunctions[functionName] ? <FaChevronDown /> : <FaChevronRight />}
              </Card.Header>
              <Collapse in={expandedLambdaFunctions[functionName]}>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={6}>
                      <div className="mb-3">
                        <strong>Function ARN:</strong>
                        <div className="text-muted small mt-1">
                          {functionLogs.function_arn}
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong>Log Group:</strong>
                        <div className="text-muted small mt-1">
                          {functionLogs.log_group}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      {functionLogs.cloudwatch_summary && (
                        <div className="mb-3">
                          <strong>CloudWatch Summary:</strong>
                          <ul className="list-unstyled mt-1 text-muted small">
                            <li>Log Streams: {functionLogs.cloudwatch_summary.log_streams}</li>
                            <li>Total Log Events: {functionLogs.cloudwatch_summary.total_log_events}</li>
                            {functionLogs.cloudwatch_summary.time_window_used && (
                              <li>Time Window: {functionLogs.cloudwatch_summary.time_window_used}</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </Col>
                  </Row>
                  
                  {functionLogs.log_streams && functionLogs.log_streams.length > 0 && (
                    <div className="log-streams-section">
                      <h6 className="mb-3">Log Streams</h6>
                      {functionLogs.log_streams.map((stream, streamIndex) => (
                        <Card key={streamIndex} className="mb-3 bg-light">
                          <Card.Header className="py-2">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <strong>Stream:</strong> {stream.streamName}
                              </div>
                              <div className="text-muted small">
                                {stream.logEvents ? stream.logEvents.length : 0} events
                              </div>
                            </div>
                            {(stream.firstEventTime || stream.lastEventTime) && (
                              <div className="text-muted small mt-1">
                                {stream.firstEventTime && (
                                  <span>First: {new Date(stream.firstEventTime).toLocaleString()}</span>
                                )}
                                {stream.firstEventTime && stream.lastEventTime && (
                                  <span className="mx-2">•</span>
                                )}
                                {stream.lastEventTime && (
                                  <span>Last: {new Date(stream.lastEventTime).toLocaleString()}</span>
                                )}
                              </div>
                            )}
                          </Card.Header>
                          <Card.Body className="p-3">
                            {stream.logEvents && stream.logEvents.length > 0 ? (
                              <div className="log-events" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                <Table size="sm" className="mb-0">
                                  <thead>
                                    <tr>
                                      <th style={{ width: '150px' }}>Timestamp</th>
                                      <th>Message</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {stream.logEvents.map((event, eventIndex) => (
                                      <tr key={eventIndex}>
                                        <td className="text-muted small">
                                          {new Date(event.timestamp).toLocaleTimeString()}
                                        </td>
                                        <td>
                                          <div style={{ 
                                            fontFamily: 'monospace', 
                                            fontSize: '0.8rem',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                          }}>
                                            {safeRenderValue(event.message)}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-muted text-center py-3">
                                No log events available
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Collapse>
            </Card>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

export default LambdaLogsCard;
